import React, { useMemo, useRef, useState } from "react";
import {
  Autocomplete as AutocompleteBase,
  TextField as TextFieldBase,
  Checkbox,
  MenuItem,
  ListItemText,
  Divider,
  Box,
  Button,
  Chip,
  Alert,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { shouldBlockUnsafeSearchInput } from "@/helpers/sanitizeHelper";

type RpAutocompleteOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

type RpAutocompleteProps = Record<string, any> & {
  options?: RpAutocompleteOption[];
  value?: string | string[] | null;
  onChange?: (_value: string | string[]) => void;
};

const Autocomplete = AutocompleteBase as React.ElementType;
const TextField = TextFieldBase as React.ElementType;

const UNCHECKED = <CheckBoxOutlineBlankIcon fontSize="small" />;
const CHECKED = <CheckBoxIcon fontSize="small" />;

// Pseudo-option values — filtered out of real option logic
const SELECT_ALL = "__SELECT_ALL__";
const CLEAR_ALL = "__CLEAR_ALL__";
const NO_OPTIONS = "__NO_OPTIONS__";

export default function RpAutocomplete({
  options = [],
  value,
  onChange,
  label = "",
  placeholder = "",
  error = false,
  helperText = "",
  staticLabel = false,
  clearLabel = "Clear All",
  multiple = false,
  disabled = false,
  size = "small",
  variant = "outlined",
  name,
  onBlur,
  inputReadOnly = false,
  noOptionsText = "No data available",
  ...rest
}: RpAutocompleteProps) {
  const { onInputChange: externalOnInputChange, ...autocompleteProps } = rest;
  const [inputValue, setInputValue] = useState("");
  const filteredOptionsRef = useRef(options);
  const stored = useMemo(
    () => (multiple ? (Array.isArray(value) ? value : []) : []),
    [multiple, value]
  );

  const selectedValue = useMemo(() => {
    if (multiple) return options.filter((o) => stored.includes(o.value));
    return options.find((o) => o.value === value) ?? null;
  }, [value, options, multiple, stored]);

  const handleChange = (
    _event: React.SyntheticEvent,
    newValue: any,
    _reason: string,
    details: any
  ) => {
    if (!multiple) {
      onChange?.(newValue?.value ?? "");
      return;
    }
    // Pseudo-options handled via button onClick
    if (
      details?.option?.value === SELECT_ALL ||
      details?.option?.value === CLEAR_ALL ||
      details?.option?.value === NO_OPTIONS
    )
      return;
    onChange?.(Array.isArray(newValue) ? newValue.map((o) => o.value) : []);
  };

  // Prepend pseudo-options only in multiple mode
  const augmentedOptions = useMemo(() => {
    if (!multiple) return options;
    if (!options.length) return [];
    return [
      { value: SELECT_ALL, label: "" },
      { value: CLEAR_ALL, label: "" },
      ...options,
    ];
  }, [options, multiple]);

  const autocompleteSx = (theme: any) => ({
    mt: "8px",
    ...(disabled &&
      !error && {
        backgroundColor: (theme.vars || theme).palette.action
          .disabledBackground,
      }),
    ...(disabled && {
      "& .MuiOutlinedInput-root": {
        color: (theme.vars || theme).palette.action.disabled,
      },
      "& .MuiAutocomplete-input.Mui-disabled, & .MuiInputBase-input.Mui-disabled":
        {
          color: (theme.vars || theme).palette.action.disabled,
          WebkitTextFillColor: (theme.vars || theme).palette.action.disabled,
        },
      "& .MuiAutocomplete-endAdornment .MuiIconButton-root": {
        color: (theme.vars || theme).palette.action.disabled,
      },
    }),
    ...rest.sx,
  });

  return (
    <Autocomplete
      options={augmentedOptions}
      multiple={multiple}
      value={selectedValue}
      getOptionLabel={(option) => option?.label ?? ""}
      isOptionEqualToValue={(option, val) => option.value === val?.value}
      // Exclude pseudo-options from keyboard navigation only
      getOptionDisabled={(option) =>
        option.value === SELECT_ALL ||
        option.value === CLEAR_ALL ||
        option.value === NO_OPTIONS ||
        option.disabled
      }
      filterOptions={(opts: RpAutocompleteOption[], state: any) => {
        const pseudo = opts.filter(
          (o) => o.value === SELECT_ALL || o.value === CLEAR_ALL
        );
        const real = opts.filter(
          (o) => o.value !== SELECT_ALL && o.value !== CLEAR_ALL
        );
        const filtered = real.filter((o) =>
          o.label.toLowerCase().includes(state.inputValue.toLowerCase())
        );
        filteredOptionsRef.current = filtered;
        if (multiple && filtered.length === 0) {
          return [...pseudo, { value: NO_OPTIONS, label: "" }];
        }
        return multiple ? [...pseudo, ...filtered] : filtered;
      }}
      size={size}
      disabled={disabled}
      noOptionsText={noOptionsText}
      openOnFocus
      autoHighlight
      disableClearable={!multiple}
      disableCloseOnSelect={multiple}
      disablePortal
      sx={autocompleteSx}
      {...autocompleteProps}
      inputValue={inputValue}
      onInputChange={(event, nextInputValue, reason) => {
        if (
          reason === "input" &&
          shouldBlockUnsafeSearchInput(nextInputValue)
        ) {
          return;
        }

        setInputValue(nextInputValue);
        externalOnInputChange?.(event, nextInputValue, reason);
      }}
      id={name}
      onBlur={onBlur}
      onChange={handleChange}
      {...(multiple && {
        renderValue: (tagValues: RpAutocompleteOption[]) => {
          if (!Array.isArray(tagValues) || tagValues.length === 0) return null;
          const [first, ...remaining] = tagValues;
          return (
            <>
              <Chip label={first.label} size="small" sx={{ maxWidth: "45%" }} />
              {remaining.length > 0 && (
                <Chip
                  label={`+${remaining.length}`}
                  size="small"
                  sx={{ ml: 0.5 }}
                />
              )}
            </>
          );
        },
      })}
      renderOption={(
        props: any,
        option: RpAutocompleteOption,
        { selected }: any
      ) => {
        const { key, ...liProps } = props;
        const filteredOptions = filteredOptionsRef.current;

        // Select All / Clear All button row
        if (option.value === SELECT_ALL) {
          return (
            <Box key="__actions__">
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  px: 1,
                  py: 0.75,
                }}
                onMouseDown={(e) => e.preventDefault()}
              >
                <Button
                  size="small"
                  variant="text"
                  color="primary"
                  disabled={!filteredOptions.length}
                  onClick={() => {
                    if (!filteredOptions.length) return;
                    onChange?.(filteredOptions.map((o) => o.value));
                  }}
                  sx={{ fontSize: "0.7rem", py: 0.25 }}
                >
                  Select All
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  disabled={!filteredOptions.length || stored.length === 0}
                  onClick={() => {
                    if (!filteredOptions.length || stored.length === 0) return;
                    onChange?.([]);
                  }}
                  sx={{ fontSize: "0.7rem", py: 0.25 }}
                >
                  {clearLabel}
                </Button>
              </Box>
              <Divider />
            </Box>
          );
        }

        // Skip CLEAR_ALL — rendered in SELECT_ALL block
        if (option.value === CLEAR_ALL) return null;

        if (option.value === NO_OPTIONS) {
          return (
            <Box key="__no_options__" sx={{ px: 1, py: 1 }}>
              <Alert severity="info" sx={{ py: 0.5, alignItems: "center" }}>
                {noOptionsText}
              </Alert>
            </Box>
          );
        }

        if (multiple) {
          return (
            <MenuItem key={key} {...liProps} dense>
              <Checkbox
                icon={UNCHECKED}
                checkedIcon={CHECKED}
                checked={selected}
                size="small"
                sx={{ py: 0, pl: 0 }}
              />
              <ListItemText primary={option.label} />
            </MenuItem>
          );
        }

        // Single select — bold the selected option
        return (
          <MenuItem key={key} {...liProps} dense>
            <ListItemText
              primary={option.label}
              slotProps={{
                primary: { fontWeight: selected ? 700 : 400 },
              }}
            />
          </MenuItem>
        );
      }}
      slotProps={{
        paper: { sx: { opacity: 1 } },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant={variant}
          placeholder={placeholder}
          inputProps={{
            ...params.inputProps,
            readOnly: inputReadOnly,
          }}
          error={error}
          helperText={helperText}
          slotProps={{
            inputLabel: {
              ...params.InputLabelProps,
              ...(staticLabel && { shrink: true }),
            },
            formHelperText: { sx: { minHeight: "1.25rem", mb: 0 } },
          }}
        />
      )}
    />
  );
}
